import { motion } from "framer-motion";
import { PHASE_PAIRS } from "@/lib/curriculum";

/**
 * PhasePath — horizontal strip showing the 4-phase pair grouping.
 * Cyan rails connect each pair. P1: A+B, P2: C+D, P3: E+F, P4: G+H.
 */
export default function PhasePath() {
    return (
        <div
            data-testid="az-phase-path"
            className="relative w-full mt-12 sm:mt-20"
        >
            {/* Eyebrow label */}
            <div className="flex items-center gap-3 justify-center mb-8">
                <span className="h-px w-10 bg-tca-cyan/40" />
                <span className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/85">
                    Phase Path · A → H
                </span>
                <span className="h-px w-10 bg-tca-cyan/40" />
            </div>

            <div className="relative max-w-3xl mx-auto">
                {/* Background rail */}
                <div
                    aria-hidden="true"
                    className="absolute top-1/2 left-[4%] right-[4%] -translate-y-1/2 h-px bg-gradient-to-r from-transparent via-tca-cyan/35 to-transparent"
                />

                <div className="relative grid grid-cols-4 gap-3 sm:gap-6">
                    {PHASE_PAIRS.map((p, i) => (
                        <motion.div
                            key={p.phase}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{
                                duration: 0.5,
                                delay: 0.08 * i,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="relative flex flex-col items-center"
                            data-testid={`phase-pair-${p.phase}`}
                        >
                            {/* Pair node */}
                            <div className="relative">
                                <div
                                    className="
                                        relative px-3 py-1.5 sm:px-4 sm:py-2
                                        border border-tca-cyan/45 bg-tca-bg-elev/90
                                        backdrop-blur-md flex items-center gap-1.5
                                    "
                                    style={{
                                        clipPath: "polygon(8% 0, 92% 0, 100% 50%, 92% 100%, 8% 100%, 0 50%)",
                                        boxShadow: "0 0 18px rgba(0, 224, 232, 0.22)",
                                    }}
                                >
                                    <span className="font-display text-tca-cyan text-base sm:text-lg leading-none">
                                        {p.letters[0]}
                                    </span>
                                    <span className="text-tca-cyan/55 text-xs">+</span>
                                    <span className="font-display text-tca-cyan text-base sm:text-lg leading-none">
                                        {p.letters[1]}
                                    </span>
                                </div>
                            </div>
                            {/* Below labels */}
                            <div className="mt-3 flex flex-col items-center">
                                <span className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-tca-cyan/80">
                                    {p.phase}
                                </span>
                                <span className="font-display italic text-tca-ink-muted text-[11px] sm:text-xs mt-0.5">
                                    {p.label}
                                </span>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
}
